import React from 'react'
import { connect } from 'react-redux'
import { TidewaysContext } from './app/context'
import { Entity } from './app/entity'
import { ErrorId, errorLevelIsNonFatal, IssueType } from './domain/Error'
import { ReleaseEvent } from './domain/Event'
import ErrorDetails from './errors/details'
import ErrorList from './errors/ErrorList'
import { ConditionalWrapper } from './helper/ConditionalWrapper'
import { ServerCapability } from './helper/ServerCapability'
import { EventDetails } from './monitor/events/EventDetails'
import { ApplicationState } from './redux/ApplicationState'
import { UpgradeNecessary } from './upgrade'
import { NoReleasesYet } from './empty-promo/NoReleasesYet'
import { Col, Row } from './ui/grid'
import { ActionExecution } from './domain/ActionExecution'
import { Alert } from './ui/alert'
import moment from 'moment'
import Navigator from './app/navigator'
import { SERVICE_ALL } from './domain/Service'

interface StateProps {
    tidewaysContext: TidewaysContext
    router: any
    defaultId?: ErrorId
    parameters: {
        error?: ErrorId
        s?: string
    }
    eventEntity: Entity<{
        event: ReleaseEvent
        relatedAlertActionExecutions: ActionExecution[]
    }>
    noEntries: boolean
    isOutOfRetention: boolean
    customWeekStartDate: string
    navigator: Navigator
}

interface OwnProps {
    level: IssueType
}

interface State {
    isOutOfRetention: boolean
}

type Props = OwnProps & StateProps

const requiredCapabilitiesByLevel = {
    deprecated: 'DeprecationTracking',
    slowsql: 'SlowSqlQueries',
    observation: 'Observations',
    'non-fatals': 'NonFatalErrors',
}

class Errors extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props)
        this.state = {
            isOutOfRetention: false,
        }
    }
    public componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<{}>, snapshot?: any): void {
        if (this.props.defaultId && !this.props.parameters.error) {
            this.props.router.goTo(this.props.tidewaysContext.route.route, { ...this.props.parameters, error: this.props.defaultId })
        }
    }

    private acknowledgeAlert() {
        this.props.navigator.replace({ weekStartDate: undefined })
    }

    public render() {
        let { error } = this.props.parameters
        const { level, noEntries } = this.props
        const customWeekStartDate = this.props.customWeekStartDate ? moment(this.props.customWeekStartDate).toDate() : undefined
        const customWeekEndDate = customWeekStartDate
            ? moment(this.props.customWeekStartDate)
                  .add(6, 'days')
                  .toDate()
            : undefined

        const requiredCapability = requiredCapabilitiesByLevel[level]

        if (level !== IssueType.OBSERVATION && !this.props.tidewaysContext.application.license.exceptionTracking) {
            return <UpgradeNecessary title={'Errors'} featureName={'Exception Tracking'} />
        }

        if (level === 'observation' && this.props.parameters?.s === SERVICE_ALL) {
            error = undefined
        }

        if (noEntries) {
            if (level === IssueType.RELEASE) {
                return (
                    <Row>
                        <Col width={6} widthMedium={8}>
                            <NoReleasesYet />
                        </Col>
                    </Row>
                )
            }
        }

        return (
            <div className="row">
                <ConditionalWrapper
                    condition={requiredCapability !== undefined}
                    wrapper={children => (
                        <ServerCapability
                            level="warning"
                            checkForActivation
                            hideChildrenOnInactive={false}
                            capability={requiredCapability || 'NonFatalErrors'}
                        >
                            {children}
                        </ServerCapability>
                    )}
                >
                    {this.state.isOutOfRetention ? (
                        <div className="tw-mb-8 col-md-12">
                            <Alert level="warning">
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                    }}
                                >
                                    The selected date range is out of retention. Showing current data instead{' '}
                                    <button className="btn btn-default" onClick={() => this.acknowledgeAlert()}>
                                        Ok
                                    </button>
                                </div>
                            </Alert>
                        </div>
                    ) : null}
                    {(level === IssueType.ERROR || level === IssueType.NON_FATALS) &&
                    !this.props.tidewaysContext.application.errorsActivated ? (
                        <div className="col-lg-9">
                            <div className="box">
                                <div className="box-header">
                                    <h3 className="box-title">Errors</h3>
                                </div>
                                <div className="box-body">
                                    <div className="row">
                                        <div className="col-lg-12">
                                            <div className="alert alert-info">
                                                <i className="fa fa-info" />
                                                <p>Collection of errors is not activated in this project.</p>
                                            </div>

                                            {level === IssueType.ERROR ? (
                                                <p>
                                                    You can activate errors for this project and then Fatal Errors and Uncaught Exceptions
                                                    are collected from your project and listed here.
                                                </p>
                                            ) : (
                                                <p>
                                                    You can activate errors for this project and then Notices, Warnings and Deprecations are
                                                    collected from your project and listed here.
                                                </p>
                                            )}

                                            {this.props.tidewaysContext.isAdmin ? (
                                                <form
                                                    method="POST"
                                                    action={this.props.router.path('XhprofOrganizationBundle.Application.activateErrors')}
                                                >
                                                    <input className="btn btn-lg btn-default" type="submit" value="Activate" />
                                                </form>
                                            ) : (
                                                <p>Only the admin of the organization can activate errors for applications.</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div>
                            <div id="error-list-panel" className="col-md-5">
                                <ErrorList level={level} start={customWeekStartDate} end={customWeekEndDate} />
                            </div>
                            <div className="col-md-7 error-details">
                                {level === IssueType.RELEASE && this.props.eventEntity?.data?.event ? (
                                    <EventDetails
                                        event={this.props.eventEntity.data.event}
                                        relatedAlertActionExecutions={this.props.eventEntity.data.relatedAlertActionExecutions}
                                    />
                                ) : null}
                                {level === IssueType.INCIDENT && !noEntries ? <ErrorDetails /> : null}
                                {level !== IssueType.RELEASE && level !== IssueType.INCIDENT && error ? <ErrorDetails /> : null}
                            </div>
                        </div>
                    )}
                </ConditionalWrapper>
            </div>
        )
    }
}

export default connect((globalState: ApplicationState, props: OwnProps) => {
    const list = globalState.errors.list || new Entity()

    const eventEntity =
        globalState.events.events && globalState.events.events[globalState.tidewaysContext.route.parameters.error]
            ? globalState.events.events[globalState.tidewaysContext.route.parameters.error]
            : new Entity()

    const parameters = globalState.tidewaysContext.route.parameters as { error?: ErrorId; level?: IssueType; s?: string }
    const service = parameters.s

    const isOutOfRetention = list.data?.isOutOfRetention
    const customWeekStartDate = list.data?.customWeekStartDate

    const firstError = list.data?.errors?.errors?.length ? list.data.errors.errors[0] : null

    let defaultId = firstError?.id

    // when switching error levels or services, it might happen that list is out of date
    // containing errors from the wrong level or service
    if (defaultId && firstError) {
        if (service && service !== SERVICE_ALL && !firstError.services.includes(service)) {
            defaultId = undefined
        }

        if (props.level === IssueType.NON_FATALS && !errorLevelIsNonFatal(firstError.level)) {
            defaultId = undefined
        }

        if (props.level !== IssueType.NON_FATALS && props.level !== firstError.level) {
            defaultId = undefined
        }
    }

    return {
        parameters,
        tidewaysContext: globalState.tidewaysContext,
        router: globalState.tidewaysContext.router,
        defaultId,
        eventEntity,
        noEntries: list.data?.errors?.errors?.length === 0,
        isOutOfRetention,
        customWeekStartDate,
        navigator: new Navigator(globalState.tidewaysContext.route),
    }
})(Errors)
